<template>
  <template v-if="check">
    <SfMessageOuterContainer
      :id="`assistant-check-${props.check.id}`"
      :dimmed="isOtherCardHighlighted"
      :highlighted="highlighted"
      data-testid="sf-assistant-check"
      type="assistant-check"
      :bleed="theme.newLayout"
      @click-outside="handleClickOutside"
      @click.stop="handleContainerClick"
    >
      <SfMessageContainer
        :read="read"
        :class="theme.newLayout && 'tw-px-6 tw-pt-4'"
      >
        <template #heading>
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sf-ai-dark-2 tw-font-medium tw-text-sm">
              {{ assistantCheckIndicatorText }}
            </span>
            <a
              v-if="effectiveContextUrl && check.contextUrlText"
              :href="effectiveContextUrl"
              class="
                tw-text-sm
                tw-whitespace-nowrap
                tw-overflow-hidden
                tw-text-ellipsis
              "
              @click.prevent.stop="handleLinkClick"
            >
              {{ check.contextUrlText }}
            </a>
          </div>
        </template>
        <template #actions>
          <RemarkActions
            :read="read"
            read-heap-tag="assistant-check-toggle-read"
            :allowed-actions="{
              openInContext: !!effectiveContextUrl,
              toggleRead: true,
            }"
            @click="handleRemarkAction"
          />
        </template>
        <template #body>
          <div
            v-dompurify-html:allowTarget="
              check.text[currentLocale]?.description
            "
            class="
              tw-text-sm
              sf-assistant-check-description
              tw-whitespace-pre-line
            "
            :class="descriptionClasses"
          ></div>
          <div class="tw-flex tw-flex-wrap tw-gap-1">
            <SfDocumentPill
              v-for="document in check.attachments"
              :id="document.id"
              :key="document.id"
              :viewing-url="document.url"
            >
              <template #left-icon>
                <i
                  class="
                    fa fa-file
                    tw-text-xs tw-leading-4 tw-text-sf-gray-light
                    hover:tw-text-sf-gray-light
                  "
                  data-testid="document-pill-file"
                ></i>
              </template>
              <template #default>
                <span :class="hideSessionClass">
                  {{ document.filename }}
                </span>
              </template>
            </SfDocumentPill>
          </div>
          <div
            v-if="check.documents && check.documents.length > 0"
            class="tw-flex tw-flex-wrap tw-gap-1"
          >
            <SfDocumentPill
              v-for="document in check.documents"
              :id="document.id"
              :key="document.id"
              :viewing-url="document.url"
            >
              <template #left-icon>
                <i
                  class="
                    fa fa-file
                    tw-text-xs tw-leading-4 tw-text-sf-gray-light
                    hover:tw-text-sf-gray-light
                  "
                  data-testid="document-pill-file"
                ></i>
              </template>
              <template #default>
                <span :class="hideSessionClass">
                  {{ document.filename }}
                </span>
              </template>
            </SfDocumentPill>
          </div>
        </template>
        <template #footer>
          <span
            v-if="read || (readByUsers && readByUsers.length > 0)"
            v-tooltip="readByUsersTooltip"
            class="
              tw-inline-block
              tw-w-auto
              tw-max-w-max
              tw-text-[11px]
              tw-text-sf-gray-dark
              tw-font-medium
              tw-uppercase
            "
          >
            {{ readByUsersDisplayText }}
          </span>
          <a
            data-track="sf-assistant-checks-details-toggle"
            class="
              tw-text-xs tw-text-sf-gray
              hover:tw-no-underline
              focus:tw-no-underline
              tw-mt-1
            "
            @click="toggleDetails"
          >
            <div v-if="hasDetails" class="tw-flex tw-items-center tw-gap-2">
              <span :class="chevronClass"></span>
              <span>{{ showDetails ? hideDetailsText : showDetailsText }}</span>
            </div>
          </a>
          <div v-if="showDetails">
            <SfAssistantCheckDetails :details="detailList" />
          </div>
        </template>
      </SfMessageContainer>

      <!-- The outer container spaces its children with tw-gap-2. On the new
           layout the comments band, the footer and the reply form stack flush,
           so they share one child and the gap moves inside it. -->
      <div
        class="tw-flex tw-flex-col"
        data-testid="assistant-check-sections"
        :class="{ 'tw-gap-2': !theme.newLayout }"
      >
        <SfCommentList
          v-if="comments.length"
          :class="{ 'tw-mt-2': !theme.newLayout }"
          :toggleable-count="toggleableComments.length"
          :bleed="theme.newLayout"
        >
          <template #toggleable>
            <SfComment
              v-for="comment in toggleableComments"
              :id="comment.id"
              :key="comment.id"
              :attachment-ids="comment.attachmentIds"
              :attachments="comment.attachments"
              :author-name="comment.authorName"
              :commentable-id="+props.check.id"
              commentable-type="AssistantCheck"
              :company-id="companyId"
              :ledger-id="ledgerId"
              :created-at="comment.createdAt"
              :editing="comment.editing"
              :edited-at="comment.editedAt"
              :read="comment.read"
              :rendered-text="comment.renderedText"
              :text="comment.text"
              border
            >
              <template #actions>
                <RemarkActions
                  :allowed-actions="comment.allowedActions"
                  @click="(e) => handleCommentAction(e, comment.id)"
                />
              </template>
            </SfComment>
          </template>

          <template #default="slotProps">
            <SfComment
              v-for="comment in visibleComments"
              :id="comment.id"
              :key="comment.id"
              :attachment-ids="comment.attachmentIds"
              :attachments="comment.attachments"
              :author-name="comment.authorName"
              :commentable-id="+props.check.id"
              commentable-type="AssistantCheck"
              :company-id="companyId"
              :ledger-id="ledgerId"
              :created-at="comment.createdAt"
              :editing="comment.editing"
              :edited-at="comment.editedAt"
              :read="comment.read"
              :rendered-text="comment.renderedText"
              :text="comment.text"
              :unread-top-offset="comment.first && !slotProps.expanded"
              :unread-bottom-offset="!comment.last"
              :border="comment.last"
            >
              <template #actions>
                <RemarkActions
                  :allowed-actions="comment.allowedActions"
                  @click="(e) => handleCommentAction(e, comment.id)"
                />
              </template>
            </SfComment>
          </template>
        </SfCommentList>

        <div
          v-if="!showNewCommentForm"
          class="tw-flex tw-items-center tw-gap-2"
          :class="{
            'tw-mt-2': !theme.newLayout,
            [DividerClasses.TOP]: theme.newLayout,
            'tw-px-3 tw-py-2': theme.newLayout,
          }"
        >
          <SfButtonDs
            v-if="showAction && check.text[currentLocale]?.action"
            size="sm"
            :data-heap="actionHeapId"
            @click="handleTriggerAction"
          >
            {{ check.text[currentLocale]?.action.title }}
          </SfButtonDs>
          <SfButtonDs class="tw-ml-auto" size="sm" @click="startReply">
            {{ I18n.t('common.reply') }}
          </SfButtonDs>
        </div>

        <CommentForm
          v-if="showNewCommentForm"
          :class="theme.newLayout && DividerClasses.TOP"
          data-testid="assistant-check-reply-form"
          commentable-type="AssistantCheck"
          :commentable-id="+check.id"
          :ledger-id="ledgerId"
          :company-id="companyId"
          :btn-text="I18n.t('common.reply')"
          :variant="theme.newLayout ? 'seamless' : 'default'"
          round-bottom
          :placeholder="
            theme.newLayout ? I18n.t('communication.comments.enter_reply') : ''
          "
          text=""
        />
      </div>
    </SfMessageOuterContainer>
  </template>
  <template v-else>
    <div>{{ I18n.t('assistant_check.not_found') }}</div>
  </template>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { Check } from '../types'
import type { Remark } from '@app/application/communication/types'
import type { RemarkActions as RemarkActionsType } from '@app/shared/vue/components/molecules/remark/types'

import { useStore } from 'vuex'
import { useCurrentElement } from '@vueuse/core'
import { onMounted, computed, ref, onBeforeUnmount } from 'vue'

import SfAssistantCheckDetails from './sf-assistant-check-details.vue'

import { SfLog } from '@app/adapters/logger'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { DividerClasses } from '@app/shared/constants'
import { commsTheme } from '@app/shared/comms-theme'
import RemarkActions from '@app/shared/vue/components/molecules/remark/remark-actions.vue'
import SfMessageContainer from '@app/shared/vue/components/sf-message/sf-message-container.vue'
import SfMessageOuterContainer from '@app/shared/vue/components/sf-message/sf-message-outer-container.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { triggerAction } from '@app/application/sf-assistant-checks/actions'
import { useNavigationStore } from '@app/shared/vue/store/navigation-store'
import { useSfAssistantChecksReadStatus } from '@app/application/sf-assistant-checks/stores/assistant-checks-read-status'
import { useSfAssistantChecksCommentIds } from '@app/application/sf-assistant-checks/stores/assistant-checks-comment-ids'

import CommentForm from '@app/application/communication/sections/communication/components/comments/comment-form.vue'
import SfCommentList from '@app/shared/vue/components/molecules/remark/sf-comment-list.vue'
import SfComment from '@app/shared/vue/components/molecules/remark/sf-comment.vue'
import { pickAttachmentsByIds } from '@app/shared/vue/components/molecules/remark/utils'
import orderBy from 'lodash/orderBy'

import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'

import {
  animatedScroll,
  getRightSidebarScrollOffset,
  rightDrawerSidepaneContainerSelector,
} from '@app/utils/domHelpers'

interface Props {
  check: Check
  dimmed?: boolean
  highlighted?: boolean
  showAction?: boolean
  read?: boolean
  readByUsers?: string[]
  remark?: Remark // TODO: This should be a fleshed out type, based on remark-card.vue
}

const theme = commsTheme()

const I18n = useI18n()
const hideSessionClass = useRedactSessionRecording()
const store = useStore()
const navigationStore = useNavigationStore()
const assistantChecksReadStatusStore = useSfAssistantChecksReadStatus()
const { toggleCheck } = assistantChecksReadStatusStore
const assistantChecksCommentIdsStore = useSfAssistantChecksCommentIds()
const { commentIdsByCheckId } = storeToRefs(assistantChecksCommentIdsStore)
const { checkReadStatus } = storeToRefs(useSfAssistantChecksReadStatus())
const highlightCardStore = useHighlightedCard()
const { highlightedCard } = storeToRefs(highlightCardStore)

const props = withDefaults(defineProps<Props>(), {
  remark: (): Remark => {
    return {} as Remark
  },
  readByUsers: (): string[] => {
    return [] as string[]
  },
})

const currentElement = useCurrentElement<HTMLElement>()

onMounted(() => {
  if (props.highlighted && currentElement.value) {
    animatedScroll(
      currentElement.value,
      rightDrawerSidepaneContainerSelector,
      getRightSidebarScrollOffset()
    )
  }

  if (window.location.pathname.includes('assistant_checks')) {
    return
  }

  const anchorMatch = window.location.hash.match(/assistant-(\d+)/)

  if (anchorMatch) {
    const checkIdFromAnchor = anchorMatch[1]
    if (String(props.check.id) === checkIdFromAnchor) {
      highlightCardStore.setHighlightedCard({
        type: 'assistant-check',
        id: checkIdFromAnchor,
      })
      if (currentElement.value) {
        animatedScroll(
          currentElement.value,
          rightDrawerSidepaneContainerSelector,
          getRightSidebarScrollOffset()
        )
      }
    }
  }

  window.eventHub.$on('highlightAssistantCheck', (checkId: number) => {
    if (String(props.check.id) === String(checkId) && currentElement.value) {
      highlightCardStore.setHighlightedCard({
        type: 'assistant-check',
        id: String(checkId),
      })

      animatedScroll(
        currentElement.value,
        rightDrawerSidepaneContainerSelector,
        getRightSidebarScrollOffset()
      )
    }
  })
})

onBeforeUnmount(() => {
  window.eventHub.$off('highlightAssistantCheck')
})

const emit =
  defineEmits<{
    clearHighlight: []
    clickedOutside: [highlighted: boolean]
  }>()

const handleToggleRead = (): void => {
  toggleCheck(props.check.id)
}

const handleTriggerAction = (): void => {
  triggerAction(props.check, currentLocale.value)
}

const showDetails = ref(false)

// Translations
const assistantCheckIndicatorText = I18n.t(
  'account_entry.assistant.check.indicator'
)
const showDetailsText = I18n.t('account_entry.assistant.check.show_details')
const hideDetailsText = I18n.t('account_entry.assistant.check.hide_details')

// computed
const highlighted = computed(() => {
  return (
    (highlightedCard.value?.type === 'assistant-check' ||
      highlightedCard.value?.type === 'assistant') &&
    String(highlightedCard.value?.id) === String(props.check.id)
  )
})

const isOtherCardHighlighted = computed(() => {
  return !!highlightedCard.value.id && !highlighted.value
})

const comments = computed(() => {
  const commentIds = commentIdsByCheckId.value[props.check.id] || []
  const commentsByIds = store.getters.commentsByIds(commentIds).filter(Boolean)

  if (commentsByIds.length === 0) {
    return []
  }

  const storeComments = orderBy(commentsByIds, ['createdAt'], ['asc'])

  return storeComments.map((comment) => ({
    ...comment,
    authorName: store.getters.userById(comment.authorId)?.name,
    attachments: pickAttachmentsByIds(
      store.getters.attachmentArray,
      comment.attachmentIds
    ),
    allowedActions: {
      remove: comment.allowedActions?.remove ?? false,
      edit: comment.allowedActions?.edit ?? false,
      openInContext: false,
      markAsRead: false,
    },
    read: checkReadStatus.value[props.check.id] === true,
    editing: store.state.ui.editCommentIds.includes(comment.id),
  }))
})

const visibleComments = computed(() => {
  const result = comments.value.slice(-2)
  return result.map((comment, i) => ({
    ...comment,
    first: i === 0,
    last: i !== result.length - 1,
  }))
})

const toggleableComments = computed(() => comments.value.slice(0, -2))

const effectiveContextUrl = computed(() => {
  if (!props.check.contextUrl) {
    return
  }

  const url = new URL(props.check.contextUrl, window.location.origin)
  url.searchParams.set('anchor', `assistant-${props.check.id}`)
  return url.toString()
})

const chevronClass = computed(() => {
  return `fa fa-chevron-${showDetails.value ? 'up' : 'down'}`
})

const descriptionClasses = computed(() => ({
  'tw-font-medium': !props.read,
}))

const detailList = computed(() => {
  const detailList = props.check.text[currentLocale.value]?.detail?.details
  return detailList || []
})

const hasDetails = computed(() => {
  return detailList.value && detailList.value.length > 0
})

const showNewCommentForm = computed(() =>
  store.state.ui.newCommentAssistantCheckIds.includes(props.check.id)
)

type availableLocales = keyof typeof props.check.text
const currentLocale = computed((): availableLocales => {
  if (I18n.locale in props.check.text) {
    return I18n.locale
  } else if (I18n.defaultLocale in props.check.text) {
    return I18n.defaultLocale
  } else {
    return (Object.keys(props.check.text)[0] as availableLocales) || 'en'
  }
})

const actionHeapIdPrefix = 'assistant-action-execute-'
const actionOpenConfirmationHeapIdPrefix = 'assistant-action-open-confirm-'

const hyphenatedActionType = computed((): string | undefined => {
  return props.check.actionType?.replace(/_/g, '-')
})

const actionHeapId = computed((): string => {
  // This is a shortcut solution to assign a custom Heap id to the action button
  // that doesn't trigger an action but opens a confirmation dialog.
  if (props.check.actionType === 'change_account_detail_template') {
    return `${actionOpenConfirmationHeapIdPrefix}${hyphenatedActionType.value}`
  }

  return `${actionHeapIdPrefix}${hyphenatedActionType.value}`
})

const readByUserNames = computed(() => {
  if (props.readByUsers.length > 0) {
    const names = props.readByUsers.map((u) => u.toUpperCase()).join(', ')

    return props.read ? ', ' + names : names
  }
  return ''
})

const readByUsersCount = computed(() => {
  return props.readByUsers.length + (props.read ? 1 : 0)
})

const readByUsersTooltip = computed(() => {
  if (readByUsersCount.value <= 3) {
    return ''
  }

  return props.read
    ? I18n.t('account_entry.assistant.check.read_by_users', {
        others: readByUserNames.value,
      })
    : I18n.t('account_entry.assistant.check.read_only_by_users', {
        others: readByUserNames.value,
      })
})

const readByUsersDisplayText = computed(() => {
  if (readByUsersCount.value > 3) {
    return props.read
      ? I18n.t('account_entry.assistant.check.read_by_others', {
          count: readByUsersCount.value - 1,
        })
      : I18n.t('account_entry.assistant.check.read_only_by_others', {
          count: readByUsersCount.value,
        })
  }

  return props.read
    ? I18n.t('account_entry.assistant.check.read_by_users', {
        others: readByUserNames.value,
      })
    : I18n.t('account_entry.assistant.check.read_only_by_users', {
        others: readByUserNames.value,
      })
})

const ledgerId = parseInt(window.Silverfin.currentLedgerId().toString(), 10)
const companyId = parseInt(window.Silverfin.currentCompanyId, 10)

function handleClickOutside(): void {
  emit('clickedOutside', highlighted.value)
}

function handleContainerClick(): void {
  if (!highlighted.value) {
    emit('clearHighlight')
  }
}

function handleOpenInContext(event: MouseEvent): void {
  navigationStore.enableBackInContext(
    window.location.href,
    'assistant',
    'check'
  )

  if (!effectiveContextUrl.value) {
    return
  }

  const shouldOpenInNewTab = event.metaKey || event.ctrlKey

  if (shouldOpenInNewTab) {
    window.open(effectiveContextUrl.value, '_blank')
  } else {
    window.Turbolinks.visit(effectiveContextUrl.value)
  }
}

function handleRemarkAction(
  action: RemarkActionsType,
  event: MouseEvent
): void {
  switch (action) {
    case 'openInContext':
      handleOpenInContext(event)
      break
    case 'toggleRead':
      handleToggleRead()
      break
    default:
      SfLog.warn(
        'SfAssistantCheck::handleRemarkAction called with invalid action.',
        {
          extra: {
            action,
          },
        }
      )
  }
}

function handleLinkClick(): void {
  if (!effectiveContextUrl.value) return

  navigationStore.enableBackInContext(
    window.location.href,
    'assistant',
    'check'
  )
  window.Turbolinks.visit(effectiveContextUrl.value)
}

function startReply(): void {
  store.dispatch('addNewCommentAssistantCheckId', props.check.id)
}

function handleCommentAction(action: string, commentId: number): void {
  if (action === 'edit') {
    store.dispatch('addEditCommentId', commentId)
  } else if (action === 'remove') {
    window.sfConfirm({
      onConfirm: () =>
        store.dispatch('removeComment', [
          props.check.id,
          'AssistantCheck',
          commentId,
        ]),
    })
  }
}

function toggleDetails(): void {
  showDetails.value = !showDetails.value
}
</script>
<style lang="scss">
.sf-assistant-check-description {
  blockquote {
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 16px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }
}
</style>
